import { useTranslation } from 'react-i18next';
import { Box, Tooltip } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import type { TeamsAvailability } from '@atlas/shared';

/**
 * Availability marker for a 1:1 conversation.
 *
 * Two rules drive the whole design:
 *
 * 1. **Colour is never the only signal.** Roughly one man in twelve cannot
 *    separate the green/red pair this convention leans on, and a high-contrast
 *    theme can flatten the palette entirely — so every state also has its own
 *    *shape* (check / plain disc / barred / clock / crossed ring). These are
 *    Teams' actual marks rather than our imitation of them, so the panel reads
 *    the same way as the client people already have open, and each is drawn in
 *    `currentColor` so it never depends on the row background it lands on
 *    (list rows change background when selected or hovered).
 * 2. **The meaning is text, not decoration.** The drawing is `aria-hidden`; a
 *    visually-hidden label carries the status to assistive tech, and the same
 *    sentence is the tooltip for everyone else.
 */

interface PresenceStyle {
  /** Fluent path data, drawn against a 20×20 viewBox. */
  path: string;
  /** Theme palette path — never a raw hex, and every one of these is defined in both modes. */
  color: string;
  /** i18n key under `teams.panel.presence.*`. */
  labelKey: string;
}

/**
 * The glyphs are **Microsoft's own**, taken from Fluent UI System Icons
 * (github.com/microsoft/fluentui-system-icons, MIT) — the same set Teams draws
 * its presence badges from, so a status here is the identical mark the reader
 * already knows from Teams rather than our approximation of it.
 *
 * Vendored as path data rather than pulled in as `@fluentui/react-icons`: five
 * paths against a whole icon package is a poor trade, and it keeps them flowing
 * through `currentColor` like every other icon in the app. Same approach as the
 * GitHub mark in `src/icons`. Copied verbatim — if one needs changing, take the
 * new version from upstream rather than editing the curve by hand.
 *
 * MIT licence, Copyright (c) Microsoft Corporation.
 *
 * `unknown` is absent on purpose: a marker meaning "we don't know" is worse
 * than no marker, because the reader has to work out that it says nothing.
 */
const PRESENCE_STYLES: Record<Exclude<TeamsAvailability, 'unknown'>, PresenceStyle> = {
  // PresenceAvailableFilled — disc with a check knocked out of it.
  available: {
    path: 'M10 20a10 10 0 1 0 0-20 10 10 0 0 0 0 20Zm4.2-11.8-4.5 4.5a1 1 0 0 1-1.4 0l-2-2a1 1 0 1 1 1.4-1.4L9 10.58l3.8-3.8a1 1 0 1 1 1.4 1.42Z',
    color: 'success.main',
    labelKey: 'teams.panel.presence.available',
  },
  // PresenceBusyFilled — a plain disc.
  busy: {
    path: 'M20 10a10 10 0 1 1-20 0 10 10 0 0 1 20 0Z',
    color: 'error.main',
    labelKey: 'teams.panel.presence.busy',
  },
  // PresenceDndFilled. Same hue as busy — the knocked-out bar is what separates
  // them, which is exactly how Teams distinguishes the two.
  doNotDisturb: {
    path: 'M10 20a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM7 9h6a1 1 0 1 1 0 2H7a1 1 0 1 1 0-2Z',
    color: 'error.main',
    labelKey: 'teams.panel.presence.doNotDisturb',
  },
  // PresenceAwayFilled — clock hands knocked out of the disc.
  away: {
    path: 'M10 20a10 10 0 1 0 0-20 10 10 0 0 0 0 20Zm0-14V9.6l2.7 2.7a1 1 0 0 1-1.4 1.42l-3-3A1 1 0 0 1 8 10V6a1 1 0 1 1 2 0Z',
    color: 'warning.main',
    labelKey: 'teams.panel.presence.away',
  },
  // PresenceOfflineRegular — ring with a cross, the one outline in the set.
  // `text.disabled` rather than a grey index: the app theme inverts the MUI
  // grey scale in dark mode, so a fixed index would flip from light to dark.
  offline: {
    path: 'M13.7 6.3a1 1 0 0 1 0 1.4L11.42 10l2.3 2.3a1 1 0 0 1-1.42 1.4L10 11.42l-2.3 2.3a1 1 0 0 1-1.4-1.42L8.58 10l-2.3-2.3a1 1 0 0 1 1.42-1.4L10 8.58l2.3-2.3a1 1 0 0 1 1.4 0ZM0 10a10 10 0 1 1 20 0 10 10 0 0 1-20 0Zm10-8a8 8 0 1 0 0 16 8 8 0 0 0 0-16Z',
    color: 'text.disabled',
    labelKey: 'teams.panel.presence.offline',
  },
};

/** Rendered size. The paths are authored at 20, so the viewBox does the scaling. */
const SIZE = 14;
const VIEW_BOX = 20;

interface TeamsPresenceDotProps {
  availability: TeamsAvailability;
}

/**
 * Renders nothing for `unknown`, so callers can pass whatever they have without
 * branching. Presentational — it fetches nothing.
 */
export function TeamsPresenceDot({ availability }: TeamsPresenceDotProps) {
  const { t } = useTranslation();

  if (availability === 'unknown') return null;
  const style = PRESENCE_STYLES[availability];

  const label = t('teams.panel.presence.label', { status: t(style.labelKey) });

  return (
    <Tooltip title={label}>
      <Box
        component="span"
        sx={{
          color: style.color,
          display: 'inline-flex',
          alignItems: 'center',
          // Both callers sit the dot in a baseline-aligned row; an inline-flex
          // box has no useful baseline, so centre it against the text instead.
          alignSelf: 'center',
          // Never allowed to squeeze the name or the timestamp in a 320px row.
          flexShrink: 0,
          lineHeight: 0,
        }}
      >
        <svg
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${VIEW_BOX} ${VIEW_BOX}`}
          aria-hidden
          focusable="false"
        >
          <path d={style.path} fill="currentColor" />
        </svg>
        <Box component="span" sx={visuallyHidden}>
          {label}
        </Box>
      </Box>
    </Tooltip>
  );
}
